import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

type DatePickerProps = {
  id?: string
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  required?: boolean
  min?: string
  max?: string
  placeholder?: string
  className?: string
}

const parseIsoDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? undefined : date
}

const toIsoDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function DatePicker({
  id,
  value,
  onChange,
  disabled,
  required,
  min,
  max,
  placeholder = "Pick a date",
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selectedDate = React.useMemo(() => parseIsoDate(value), [value])
  const minDate = React.useMemo(() => parseIsoDate(min), [min])
  const maxDate = React.useMemo(() => parseIsoDate(max), [max])

  const now = new Date()
  const defaultStartYear = selectedDate
    ? Math.min(selectedDate.getFullYear() - 10, now.getFullYear() - 10)
    : now.getFullYear() - 10
  const defaultEndYear = selectedDate
    ? Math.max(selectedDate.getFullYear() + 10, now.getFullYear() + 5)
    : now.getFullYear() + 5
  const startMonth = minDate ?? new Date(defaultStartYear, 0, 1)
  const endMonth = maxDate ?? new Date(defaultEndYear, 11, 1)

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      const isoDate = toIsoDate(date)
      if (minDate && isoDate < toIsoDate(minDate)) return true
      if (maxDate && isoDate > toIsoDate(maxDate)) return true
      return false
    },
    [maxDate, minDate]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          aria-required={required}
          variant="outline"
          disabled={disabled}
          className={cn(
            "min-w-0 w-full justify-start overflow-hidden text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="min-w-0 truncate">
            {selectedDate ? format(selectedDate, "PPP") : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          selected={selectedDate}
          onSelect={(date) => {
            if (date) {
              onChange(toIsoDate(date))
              setOpen(false)
            }
          }}
          disabled={minDate || maxDate ? isDateDisabled : undefined}
        />
      </PopoverContent>
    </Popover>
  )
}
